/**
 * WP-V2-CS — katalog obiektów Kawiarni „Kość" (PLAN-V2 §6 Cafe).
 * Silnik: interact/ (ObjectDef); sceneria tylko konsumuje.
 */
import type { ObjectDef, SnapZone, SpawnPlacement } from '../../interact/index.js';
import type { ColliderRect } from '../../core/world/index.js';

/** Świat wnętrza — PLAN-V2 §4.2. */
export const CAFE_WORLD_W = 1600;
export const CAFE_WORLD_H = 720;

export const CAFE_COOKIE_DEF_ID = 'cafe_cupcake';
export const CAFE_QUEST_ID = 'cafe_cupcakes';

/** Definicje typów obiektów kawiarni (≥8 interaktywnych). */
export const CAFE_OBJECT_DEFS: ObjectDef[] = [
  {
    id: CAFE_COOKIE_DEF_ID,
    texture: 'obj_cafe_cupcake',
    size: 72,
    interactions: ['pickup', 'give', 'consume', 'drag'],
    states: ['full', 'empty'],
    sound: 'chomp',
    fx: 'crumbs',
    dogReaction: 'eat',
    snapTargets: ['cafe_tray', 'cafe_table'],
  },
  {
    id: 'cafe_espresso',
    texture: 'obj_cafe_espresso',
    size: 96,
    interactions: ['tap'],
    states: ['idle', 'brewing'],
    sound: 'drip',
    fx: 'sparkle',
    dogReaction: 'sniff',
  },
  {
    id: 'cafe_tray',
    texture: 'obj_cafe_tray',
    size: 88,
    interactions: ['tap'],
    fx: 'sparkle',
  },
  {
    id: 'cafe_pitcher',
    texture: 'obj_cafe_pitcher',
    size: 80,
    interactions: ['tap', 'toggle'],
    states: ['full', 'empty'],
    sound: 'pour',
    fx: 'splash',
    dogReaction: 'happy',
  },
  {
    id: 'cafe_chair_a',
    texture: 'obj_cafe_chair',
    size: 80,
    interactions: ['tap'],
    sound: 'sit',
    fx: 'hearts',
    // brak dogReaction celowo: CafeScene.onInteract odpala dedykowane playAction('sit') —
    // generyczny dogReaction 'happy' nadpisywałby je w tym samym ticku (siadanie nigdy
    // by się nie pokazało).
  },
  {
    id: 'cafe_chair_b',
    texture: 'obj_cafe_chair',
    size: 80,
    interactions: ['tap'],
    sound: 'sit',
    fx: 'hearts',
  },
  {
    id: 'cafe_bell',
    texture: 'obj_cafe_bell',
    size: 72,
    interactions: ['tap'],
    sound: 'ding',
    fx: 'notes',
    dogReaction: 'jump',
  },
  {
    id: 'cafe_menu',
    texture: 'obj_cafe_menu',
    size: 72,
    interactions: ['tap'],
    fx: 'stars',
    dogReaction: 'sniff',
  },
  {
    id: 'cafe_sugar',
    texture: 'obj_cafe_sugar',
    size: 68,
    interactions: ['pickup', 'drag', 'give'],
    fx: 'sparkle',
    dogReaction: 'sniff',
    snapTargets: ['cafe_table'],
  },
  {
    id: 'cafe_plant',
    texture: 'obj_cafe_plant',
    size: 72,
    interactions: ['tap'],
    fx: 'stars',
    dogReaction: 'sniff',
  },
];

/** 6 ciastek na ladzie + reszta lokalu. */
export const CAFE_PLACEMENTS: SpawnPlacement[] = [
  // lada — 6 ciastek
  { defId: CAFE_COOKIE_DEF_ID, x: 420, y: 330 },
  { defId: CAFE_COOKIE_DEF_ID, x: 510, y: 330 },
  { defId: CAFE_COOKIE_DEF_ID, x: 600, y: 330 },
  { defId: CAFE_COOKIE_DEF_ID, x: 690, y: 330 },
  { defId: CAFE_COOKIE_DEF_ID, x: 780, y: 330 },
  { defId: CAFE_COOKIE_DEF_ID, x: 870, y: 330 },
  // ekspres, taca, dzbanek, dzwonek
  { defId: 'cafe_espresso', x: 1040, y: 300 },
  { defId: 'cafe_tray', x: 320, y: 340 },
  { defId: 'cafe_pitcher', x: 960, y: 340 },
  { defId: 'cafe_bell', x: 880, y: 280 },
  // krzesła + dekor
  { defId: 'cafe_chair_a', x: 380, y: 520 },
  { defId: 'cafe_chair_b', x: 620, y: 540 },
  { defId: 'cafe_menu', x: 200, y: 360 },
  { defId: 'cafe_sugar', x: 740, y: 360 },
  { defId: 'cafe_plant', x: 1480, y: 480 },
];

export const CAFE_SNAP_ZONES: SnapZone[] = [
  { id: 'cafe_tray', x: 320, y: 340, radius: 56 },
  { id: 'cafe_table', x: 500, y: 500, radius: 64 },
];

/** Lada i ściany — lekkie blokady ruchu. */
export const CAFE_COLLIDERS: ColliderRect[] = [
  { x: 280, y: 300, width: 720, height: 40 }, // lada
  { x: 0, y: 0, width: CAFE_WORLD_W, height: 100 }, // ściana górna
];

export function countCookiePlacements(
  placements: readonly SpawnPlacement[] = CAFE_PLACEMENTS,
): number {
  return placements.filter((p) => p.defId === CAFE_COOKIE_DEF_ID).length;
}

export function isCafeCookieDef(defId: string): boolean {
  return defId === CAFE_COOKIE_DEF_ID;
}
